/**
 * ATL-1793 — say WHICH products made Firestore refuse a write batch.
 *
 * WHY THIS EXISTS. A stock-affecting writer commits one Firestore batch that `update`s every
 * product it touches (`batch.update(productRef, { 'storeStock.<store>': … })`). `update` requires
 * the document to exist and a batch is atomic, so ONE product whose Firestore document is gone
 * makes the server refuse the WHOLE operation with `not-found` — nothing lands. On a cut-over
 * organization that product is still listed, because the till reads Supabase and a delete that
 * never reached Supabase (the ATL-902 class) leaves the row alive there. Measured 2026-10-23:
 * 7 such products on 5 cut-over orgs. Brooklyn shop lost an hour-long revision to one of them, and
 * the screen said only «Ошибка… обратитесь в поддержку» — no retry could ever succeed and nothing
 * told the merchant which row to leave out.
 *
 * WHAT IT DOES. Only after the server has REFUSED the batch with `not-found` — never before a
 * commit, so an offline save still queues exactly as it did — it reads each product the batch
 * updated from the SERVER and returns the ones whose document does not exist. The caller wraps
 * them in `ProductDocumentsMissingError` so the screen can name them.
 *
 * WHY THE READ IS `source: 'server'` (as in `deletedEntityLocation.ts`): on native the default is
 * the device cache, and a cut-over device never cached these products from Firestore, so a cached
 * read would answer about nothing it knows. A read that fails or outruns the cap means "unknown",
 * and unknown is reported as the original error, unchanged — never as a guessed product list.
 *
 * WHAT IT DOES NOT DO. It does not drop the products from the batch and retry: a counted product
 * that the master has deleted is a decision for the merchant (and the resurrected row is a data
 * defect healed on its own, ATL-1793), not something a writer may silently leave out of the books.
 */
import { getFirestoreRef } from './firestoreRefUtils'
import { snapshotExists } from './types'
import { ProductDocumentsMissingError, type MissingProduct } from './productDocumentsMissingError'
import { isFirestoreNotFound } from './firestoreNotFound'

export { ProductDocumentsMissingError, type MissingProduct }
export { isFirestoreNotFound }

/** How long the post-failure probe may take before the original error is reported instead. */
const PROBE_TIMEOUT_MS = 10_000


type ProductLike = { id?: string; firestoreId?: string; name?: string }
type ServerReadable = { get: (options: { source: 'server' }) => Promise<{ exists?: unknown } | null | undefined> }

const PROBE_TIMED_OUT = Symbol('probe-timed-out')

/** true = the document exists, false = it does not, null = the server did not answer. */
const probeExists = async (product: ProductLike): Promise<boolean | null> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const ref = getFirestoreRef(product, 'products') as ServerReadable
    const answer = await Promise.race([
      ref.get({ source: 'server' }),
      new Promise<typeof PROBE_TIMED_OUT>((resolve) => {
        timer = setTimeout(() => resolve(PROBE_TIMED_OUT), PROBE_TIMEOUT_MS)
      }),
    ])
    // `snapshotExists` reads a missing snapshot as "absent"; here it is "no answer".
    if (answer === PROBE_TIMED_OUT || !answer) return null
    return snapshotExists(answer)
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}

/**
 * The products among `products` whose Firestore document does not exist, or null when that cannot
 * be established (any read failed or timed out, or none is missing — the refusal was about
 * something else). Each product is read once.
 */
export const findProductsMissingFromFirestore = async (
  products: ProductLike[],
): Promise<MissingProduct[] | null> => {
  const unique = new Map<string, ProductLike>()
  for (const product of products) {
    const id = product?.firestoreId ?? product?.id
    if (id && !unique.has(id)) unique.set(id, product)
  }
  const entries = [...unique.entries()]
  const answers = await Promise.all(entries.map(([, product]) => probeExists(product)))
  if (answers.some((answer) => answer === null)) return null
  const missing = entries
    .filter((_, index) => answers[index] === false)
    .map(([id, product]) => ({ id, name: product.name || id }))
  return missing.length > 0 ? missing : null
}

/**
 * What a writer should throw for a refused commit: `ProductDocumentsMissingError` when the refusal
 * is `not-found` AND the server confirms which of `products` are gone; otherwise `error` unchanged.
 */
export const explainRefusedCommit = async (
  writer: string,
  error: unknown,
  products: ProductLike[],
): Promise<unknown> => {
  if (!isFirestoreNotFound(error)) return error
  const missing = await findProductsMissingFromFirestore(products)
  return missing ? new ProductDocumentsMissingError(writer, missing, error) : error
}
